import type { CalendarDate } from "./calendar";

/**
 * The catalog: what exists. Pots (arrangements), clients, and the spots ("slots") at each
 * client where an arrangement stands. Validated once on load, so everything downstream can
 * trust that ids resolve.
 */

/**
 * Descriptive attributes, e.g. { colour: ["white", "green"], size: ["large"] }.
 * Generic on purpose: a new attribute (style, flower type) is data, not a code change.
 */
export type PotAttributes = Readonly<Record<string, readonly string[]>>;

/** Purchase details for the asset register. Money in whole cents (ZAR) so totals never drift. */
export interface PotAsset {
  readonly boughtOn?: CalendarDate;
  readonly costCents?: number;
  readonly supplier?: string;
}

export interface Pot {
  /** The number on the sticker, e.g. "A1". */
  readonly id: string;
  readonly attributes: PotAttributes;
  /** Retired or broken arrangements stay in history but can't be placed. */
  readonly active: boolean;
  /** Asset register: what it cost and when, for the books (optional until imported). */
  readonly asset?: PotAsset;
}

/**
 * A client requirement about what an arrangement may look like. Shown as amber (a warning), not a block.
 * - "only":    every value the pot has for `attribute` must be one of `values`.
 *              A pot with no value for the attribute doesn't satisfy it (we can't tell).
 *              "Only white flowers" → { attribute: "colour", rule: "only", values: ["white"] }
 * - "exclude": the pot may have none of `values`.
 *              "No red" → { attribute: "colour", rule: "exclude", values: ["red"] }
 */
export interface Requirement {
  readonly attribute: string;
  readonly rule: "only" | "exclude";
  readonly values: readonly string[];
  /** Arlene's own words, shown with the warning. */
  readonly note?: string;
}

export interface Client {
  readonly id: string;
  readonly name: string;
  /** Apply to every spot at this client. */
  readonly requirements: readonly Requirement[];
}

export interface Slot {
  readonly id: string;
  readonly clientId: string;
  /** "Reception", "Boardroom", ... */
  readonly label: string;
  /** Apply to this spot only, on top of the client's requirements. */
  readonly requirements: readonly Requirement[];
}

export interface Catalog {
  pot(id: string): Pot | undefined;
  client(id: string): Client | undefined;
  /** @throws for an unknown slot id: slots come from her own route, so this is a programming error. */
  slot(id: string): Slot;
  readonly pots: readonly Pot[];
  readonly slots: readonly Slot[];
}

function indexById<T extends { id: string }>(items: readonly T[], kind: string): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    if (map.has(item.id)) throw new Error(`Duplicate ${kind} id "${item.id}"`);
    map.set(item.id, item);
  }
  return map;
}

/** The catalog as plain data: what she edits and what is saved. */
export interface CatalogData {
  readonly pots: readonly Pot[];
  readonly clients: readonly Client[];
  readonly slots: readonly Slot[];
}

/** Build a catalog from saved data. */
export function catalogFrom(data: CatalogData): Catalog {
  return createCatalog(data.pots, data.clients, data.slots);
}

/** @throws on duplicate ids or a slot that points at an unknown client. */
export function createCatalog(pots: readonly Pot[], clients: readonly Client[], slots: readonly Slot[]): Catalog {
  const potById = indexById(pots, "pot");
  const clientById = indexById(clients, "client");
  const slotById = indexById(slots, "slot");
  for (const slot of slots) {
    if (!clientById.has(slot.clientId)) {
      throw new Error(`Slot "${slot.id}" belongs to unknown client "${slot.clientId}"`);
    }
  }
  return {
    pot: (id) => potById.get(id),
    client: (id) => clientById.get(id),
    slot(id) {
      const slot = slotById.get(id);
      if (!slot) throw new Error(`Unknown slot "${id}"`);
      return slot;
    },
    pots,
    slots,
  };
}
